<template>
  <div class="widget-detail-panel">
    <div class="widget-detail-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="handleClose"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2
        v-if="!loading"
        class="widget-detail-title"
      >
        {{ panelTitle }}
      </h2>
      <div
        v-else
        class="skeleton-line skeleton-line--title"
      />
    </div>

    <div class="widget-detail-content">
      <!-- Skeleton loader -->
      <div
        v-if="loading"
        class="practices-skeleton"
      >
        <div class="skeleton-block">
          <div class="skeleton-line skeleton-line--short" />
          <div class="skeleton-blockquote">
            <div class="skeleton-line skeleton-line--long" />
            <div class="skeleton-line skeleton-line--medium" />
          </div>
        </div>
        <div class="skeleton-line skeleton-line--badge" />
        <div class="skeleton-card" />
      </div>

      <!-- Main content -->
      <template v-else-if="panelData">
        <!-- Best practice section -->
        <div class="best-practice-section">
          <div class="best-practice-header">
            <h3 class="best-practice-heading">
              Best practice
            </h3>
            <span
              :class="[
                'status-badge',
                panelData.state === 'covered' ? 'status-badge--automated' : panelData.state === 'missed' ? 'status-badge--not-automated' : 'status-badge--not-automated-grey'
              ]"
            >
              {{ panelData.state === 'covered' ? 'Automated' : 'Not automated' }}
            </span>
          </div>
          <blockquote class="best-practice-quote">
            {{ panelData.description }}
          </blockquote>
        </div>

        <!-- Missed opportunity card (state 1 only) -->
        <div
          v-if="panelData.state === 'missed'"
          class="missed-opportunity-card"
        >
          <div class="missed-opportunity-count">
            {{ panelData.opportunityCount }}
          </div>
          <div class="missed-opportunity-text">
            {{ panelData.missedDescription }}
          </div>
        </div>

        <!-- Covered stat card (state 3 only) -->
        <div
          v-if="panelData.state === 'covered'"
          class="stat-card"
        >
          <div class="stat-card-value">
            {{ panelData.proofCount }}
          </div>
          <div class="stat-card-label">
            {{ panelData.coveredDescription }}.
            <a
              v-if="panelData.workflowId"
              href="#"
              class="view-automation-link"
              @click.prevent="viewAutomation(panelData.workflowId)"
            >View automation</a>
          </div>
        </div>

        <!-- Automate this (states 1 & 2 only) -->
        <div
          v-if="panelData.state !== 'covered'"
          class="template-section"
        >
          <h3 class="template-heading">
            {{ panelData.recipe ? 'Recommended automation template' : 'Set up an automation' }}
          </h3>
          <button
            v-if="panelData.recipe"
            type="button"
            class="template-card"
            @click="enableAutomation(panelData.recipe)"
          >
            <span
              class="template-card-icon"
              :style="{ background: panelData.recipeStyle.bg }"
            >{{ panelData.recipeStyle.emoji }}</span>
            <div class="template-card-content">
              <div class="template-card-name">
                {{ panelData.recipe.name }}
              </div>
              <div
                v-if="panelData.recipeStyle.desc"
                class="template-card-desc"
              >
                {{ panelData.recipeStyle.desc }}
              </div>
            </div>
            <span class="template-card-arrow">&#x203A;</span>
          </button>
          <button
            v-else
            type="button"
            class="template-card"
            @click="navigateToWorkflows('/automations/templates')"
          >
            <span
              class="template-card-icon"
              :style="{ background: '#DBEDDB' }"
            >&#x26A1;</span>
            <div class="template-card-content">
              <div class="template-card-name">
                Create an automation
              </div>
              <div class="template-card-desc">
                Set up a workflow to handle this automatically.
              </div>
            </div>
            <span class="template-card-arrow">&#x203A;</span>
          </button>
        </div>
      </template>

      <!-- Empty state -->
      <div
        v-else
        class="practices-empty"
      >
        No automation data for this widget
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useBestPracticesData, getFooterStateForWidget, getStateForBestPracticeType } from '../composables/useBestPracticesData.js'
import { BEST_PRACTICE_COPY, formatBestPracticeCopy } from '../utils/widgetBestPractices.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  widgetType: {
    type: String,
    default: null
  },
  bestPracticeType: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['close'])

const trackPosthogEvent = inject('trackPosthogEvent', () => {})

const { guard } = useTrialModeGuard()
const { loading } = useBestPracticesData()

// Recipe descriptions for the template card
const RECIPE_STYLES = {
  STAY_ON_TOP_OF_ISSUES: { emoji: '\u2705', bg: '#DBEDDB', desc: 'Address issues in your community as soon as they arise.' },
  ONBOARD_NEW_USERS: { emoji: '\uD83D\uDC31', bg: '#FEF3C7', desc: 'Create a seamless user onboarding experience from start to finish.' },
  RETARGET_UNCONFIRMED: { emoji: '\uD83C\uDFAF', bg: '#FFE4E6', desc: 'Reduce the drop-off rate of potential new users.' },
  CHECK_IN_NEW_MEMBERS: { emoji: '\u2764\uFE0F', bg: '#FFE4E6', desc: 'A personal touch goes a long way in welcoming new users.' },
  FOLLOW_UP_INQUIRIES: { emoji: '\uD83D\uDCAC', bg: '#DBEAFE', desc: 'Fast follow-ups are key to a smoother sales process.' },
  BEGIN_OFFBOARDING: { emoji: '\uD83D\uDC4B', bg: '#FEF3C7', desc: 'Offboarding is your last chance to make a great impression.' },
  COMPLETE_OFFBOARDING: { emoji: '\uD83D\uDCCB', bg: '#FFEDD5', desc: 'Tie up any loose ends when a user exits your community.' },
  PREPARE_DROP_IN_BOOKERS: { emoji: '\uD83D\uDCDD', bg: '#FFE4E6', desc: 'Improve the drop-in experience by automatically sending reminder emails with key visit details.' },
  FEEDBACK_AFTER_X_BOOKINGS: { emoji: '\u2B50', bg: '#FEF3C7', desc: 'Embrace an agile iterative culture. Schedule a survey to go out after a set number of bookings.' },
  REWARD_FREQUENT_BOOKERS: { emoji: '\uD83C\uDFC6', bg: '#FEF3C7', desc: 'Automatically rewarding loyal users.' },
  RE_ENGAGE_USERS: { emoji: '\uD83D\uDCF1', bg: '#DBEDDB', desc: 'Reactivate inactive users and invite them back to the community.' },
  REMIND_USERS_TO_BOOK: { emoji: '\uD83D\uDD04', bg: '#DBEDDB', desc: 'A friendly nudge could be the difference between a casual drop-in and a long-term user.' },
  RETARGET_DROP_IN: { emoji: '\uD83D\uDCCA', bg: '#DBEDDB', desc: 'Turn first-time visitors into repeat users by encouraging them to book again.' },
  CONVERT_TOUR_TO_SIGNUP: { emoji: '\uD83D\uDDD2\uFE0F', bg: '#FFE4E6', desc: 'Create a seamless, high-touch tour experience by automatically confirming tours, sending reminders, and following up.' },
  CONVERT_PROSPECTS_FROM_TOUR_TO_SIGN_UP: { emoji: '\uD83D\uDDD2\uFE0F', bg: '#FFE4E6', desc: 'Create a seamless, high-touch tour experience by automatically confirming tours, sending reminders, and following up.' },
  LATE_FEES_OVERDUE: { emoji: '\u26A0\uFE0F', bg: '#FEF3C7', desc: 'Reduce manual follow-ups by sending emails to the user automatically, applying late fees when an invoice remains overdue, and escalating to an internal admin for follow-up if the invoice is still unpaid. This Automation will use 3 Automation actions (including one Automations+ action) per account enrolled.' },
  ONBOARDING: { emoji: '\u2764\uFE0F', bg: '#FFE4E6', desc: 'A personal touch goes a long way in welcoming new users.' },
  OFFBOARDING: { emoji: '\uD83D\uDC4B', bg: '#FEF3C7', desc: 'Start the conversation before they leave.' },
  NEW_INQUIRY: { emoji: '\uD83D\uDCAC', bg: '#DBEAFE', desc: 'Fast follow-ups are key to a smoother sales process.' }
}

const DEFAULT_RECIPE_STYLE = { emoji: '\u26A1', bg: '#DBEDDB', desc: '' }

const panelData = computed(() => {
  const footerState = props.bestPracticeType
    ? getStateForBestPracticeType(props.bestPracticeType)
    : props.widgetType ? getFooterStateForWidget(props.widgetType) : null
  if (!footerState) return null

  const bp = footerState.bestPractice
  console.log('[WidgetDetailPanel] recipe_type:', bp.covering_workflow_recipe?.recipe_type, 'full recipe:', bp.covering_workflow_recipe)
  const copy = BEST_PRACTICE_COPY[bp.type]

  const base = {
    state: footerState.state,
    description: copy?.description || '',
    recipe: bp.covering_workflow_recipe || null,
    recipeStyle: bp.covering_workflow_recipe
      ? (RECIPE_STYLES[bp.covering_workflow_recipe.recipe_type] || DEFAULT_RECIPE_STYLE)
      : DEFAULT_RECIPE_STYLE,
    workflowId: bp.covering_workflow?.workflow_id || null
  }

  if (footerState.state === 'missed') {
    const n = bp.opportunity_count ?? 0
    return {
      ...base,
      opportunityCount: String(n),
      missedDescription: formatBestPracticeCopy(copy?.missedDetailed, bp.opportunity_count)
    }
  }

  if (footerState.state === 'covered') {
    const count = bp.proof_count ?? 0
    return {
      ...base,
      proofCount: count,
      coveredDescription: (count === 0 && copy?.coveredZero)
        ? copy.coveredZero
        : formatBestPracticeCopy(copy?.coveredDetailed, count, copy?.maxCount)
    }
  }

  // noCoverage
  return base
})

const panelTitle = computed(() => {
  if (panelData.value?.state === 'covered') return 'Automations working for you'
  return 'Ready to automate'
})

const handleClose = () => {
  trackPosthogEvent('Dashboard: Automation panel closed', { widgetType: props.widgetType, state: panelData.value?.state })
  emit('close')
}

const navigateToWorkflows = (path = '/workflows') => {
  trackPosthogEvent('Dashboard: Automation panel create clicked', { widgetType: props.widgetType, path })
  try {
    let clientId = ''
    let canvasUUID = ''
    const urlParams = new URLSearchParams(window.location.search)
    clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
    canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
    if (document.referrer) {
      try {
        const referrerParams = new URLSearchParams(new URL(document.referrer).search)
        if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      } catch (_e) {
        // Ignore
      }
    }
    const finalCanvasUUID = canvasUUID || 'default-canvas-uuid'
    const message = {
      command: 'ReplaceUrl',
      payload: { client_id: clientId || '', canvasUUID: finalCanvasUUID, url: path, reload: true }
    }
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*')
      if (window.top && window.top !== window && window.top !== window.parent) {
        window.top.postMessage(message, '*')
      }
    } else {
      // Fallback for standalone mode (e.g. Cursor browser)
      try {
        const parentOrigin = document.referrer ? new URL(document.referrer).origin : window.location.origin
        window.open(parentOrigin + path, '_blank')
      } catch (_e) {
        window.open(path, '_blank')
      }
    }
  } catch (e) {
    console.error('Failed to open workflows:', e)
  }
}

const enableAutomation = (recipe) => {
  trackPosthogEvent('Dashboard: Automation panel template clicked', { widgetType: props.widgetType, recipeType: recipe?.recipe_type })
  if (!recipe) {
    navigateToWorkflows('/automations/templates')
    return
  }
  try {
    const urlParams = new URLSearchParams(window.location.search)
    let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
    let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''

    if (!canvasUUID && window.name) {
      const nameMatch = window.name.match(/^\d+-\d+$/)
      if (nameMatch) canvasUUID = window.name
    }

    if (!clientId || !canvasUUID) {
      if (document.referrer) {
        try {
          const referrerParams = new URLSearchParams(new URL(document.referrer).search)
          if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
          if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
        } catch (_e) {
          // Ignore
        }
      }
    }

    if (!canvasUUID) {
      const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
      canvasUUID = `${Date.now()}-${random}`
    }

    const message = {
      command: 'RecipeView',
      payload: {
        client_id: clientId || undefined,
        recipe_type: recipe.recipe_type,
        canvasUUID
      }
    }

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open recipe panel:', e)
  }
}

const openWorkflowPanel = (workflowId) => {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
    let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''

    if (!canvasUUID && window.name) {
      const nameMatch = window.name.match(/^\d+-\d+$/)
      if (nameMatch) canvasUUID = window.name
    }

    if (!clientId || !canvasUUID) {
      if (document.referrer) {
        try {
          const referrerParams = new URLSearchParams(new URL(document.referrer).search)
          if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
          if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
        } catch (_e) {
          // Ignore
        }
      }
    }

    if (!canvasUUID) {
      const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
      canvasUUID = `${Date.now()}-${random}`
    }

    const message = {
      workflowId: workflowId.toString(),
      canvasUUID,
      client_id: clientId || undefined
    }

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*')
      setTimeout(() => {
        window.parent.postMessage({ command: 'WorkflowView', payload: message }, '*')
      }, 50)
    }
  } catch (e) {
    console.error('Failed to open workflow panel:', e)
  }
}

const viewAutomation = guard((workflowId) => {
  trackPosthogEvent('Dashboard: Automation panel view automation clicked', { widgetType: props.widgetType, workflowId })
  if (workflowId) {
    openWorkflowPanel(workflowId)
  } else {
    navigateToWorkflows()
  }
})
</script>

<style scoped>
.widget-detail-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.widget-detail-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: rgba(0, 0, 0, 0.87);
}

.widget-detail-title {
  flex: 1;
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.widget-detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px 24px 24px 72px;
}

/* Best practice section */
.best-practice-section {
  margin-bottom: 20px;
}

.best-practice-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.best-practice-heading {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.best-practice-quote {
  margin: 0;
  padding: 0;
  border-left: none;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.75);
}

/* Status badge */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.status-badge--automated {
  background: var(--color-bg-green, #dcfce7);
  color: #1C3829;
}

.status-badge--not-automated {
  background: var(--color-bg-yellow, #FEF3C7);
  color: var(--color-yellow-text, #92400e);
  border: none;
}

.status-badge--not-automated-grey {
  background: rgba(241, 240, 239, 1);
  color: #6b7280;
  border: none;
}

/* Missed opportunity card */
.missed-opportunity-card {
  background: var(--color-bg-yellow, #FEF3C7);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.missed-opportunity-count {
  font-size: 24px;
  font-weight: 500;
  color: var(--color-yellow-text, #92400e);
  line-height: 28px;
}

.missed-opportunity-text {
  font-size: 14px;
  font-weight: 400;
  color: var(--color-yellow-text, #92400e);
  margin-top: 4px;
  line-height: 21px;
  letter-spacing: 0.13px;
}

/* Covered stat card */
.stat-card {
  background: #f5f5f4;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
}

.stat-card-value {
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 28px;
}

.stat-card-label {
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
  line-height: 21px;
  letter-spacing: 0.13px;
}

/* Template section */
.template-section {
  margin-top: 24px;
}

.template-heading {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 12px 0;
}

.template-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 16px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.template-card:hover {
  border-color: rgba(0, 0, 0, 0.24);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.template-card-icon {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  font-size: 20px;
}

.template-card-content {
  flex: 1;
  min-width: 0;
}

.template-card-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
}

.template-card-desc {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
  line-height: 1.4;
}

.template-card-arrow {
  font-size: 24px;
  color: rgba(0, 0, 0, 0.3);
  margin-left: auto;
  align-self: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.template-card:hover .template-card-arrow {
  opacity: 1;
}

/* View automation link */
.view-automation-link {
  color: #2383E2;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.view-automation-link:hover {
  color: #1a6bc0;
}

/* Empty state */
.practices-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

/* Skeleton loader */
.practices-skeleton {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-blockquote {
  padding-left: 16px;
  border-left: 3px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 4px;
  height: 14px;
}

.skeleton-line--long { width: 90%; }
.skeleton-line--medium { width: 60%; }
.skeleton-line--short { width: 30%; }
.skeleton-line--badge { width: 100px; height: 24px; border-radius: 4px; }
.skeleton-line--title { width: 200px; height: 24px; border-radius: 4px; flex: none; }

.skeleton-card {
  width: 100%;
  height: 80px;
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 10px;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
</style>
